/**
 * Pure mutation functions for asset-related document changes.
 *
 * These functions are used by the direct store methods (setAssets, removeAssets)
 * in document.ts to mutate document drafts inside Immer producers.
 */

import type {IDocumentData} from "@buildwithflux/models";

/**
 * Pure mutation function: adds or updates assets in the document draft.
 * Exported for use by direct store methods.
 */
export function applySetAssets(
    draft: IDocumentData,
    setAssetsPayload: Record<string, IDocumentData["assets"][string]>,
): void {
    const assets = draft.assets;

    for (const assetData of Object.values(setAssetsPayload)) {
        assets[assetData.uid] = assetData;

        if (assetData.isThumbnail) {
            draft.preview_image = assetData.storageName ?? "";
        }
    }
}

/**
 * Pure mutation function: removes assets from the document draft by their UIDs.
 * Returns true if any assets were actually deleted.
 * Exported for use by direct store methods.
 */
export function applyRemoveAssets(draft: IDocumentData, removeAssetUids: string[]): void {
    const assets = draft.assets;

    const assetsToBeDeleted = removeAssetUids
        .map((assetUid) => assets[assetUid])
        .filter((asset): asset is NonNullable<typeof asset> => asset != null);

    if (assetsToBeDeleted.length === 0) {
        return;
    }

    for (const asset of assetsToBeDeleted) {
        if (draft.preview_image === asset.storageName) {
            draft.preview_image = "";
        }

        delete assets[asset.uid];
    }
}
